import type { ReactNode } from "react";
import { genreList, regionList, yearRanges } from "@/mocks/movies";

export interface Filters {
  genres: string[];
  yearRange: string;
  minRating: number;
  region: string;
}

interface FilterPanelProps {
  filters: Filters;
  resultCount: number;
  onToggleGenre: (genre: string) => void;
  onSetYear: (label: string) => void;
  onSetRating: (value: number) => void;
  onSetRegion: (region: string) => void;
  onReset: () => void;
}

const ratingOptions = [
  { label: "全部评分", value: 0 },
  { label: "9.0分以上", value: 9 },
  { label: "8.0分以上", value: 8 },
  { label: "7.0分以上", value: 7 },
];

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-md border px-3 py-1.5 font-label text-xs transition-colors ${
        active
          ? "border-accent-500 bg-accent-500 text-background-50"
          : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterPanel({
  filters,
  resultCount,
  onToggleGenre,
  onSetYear,
  onSetRating,
  onSetRegion,
  onReset,
}: FilterPanelProps) {
  return (
    <aside className="space-y-6 rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-bold text-foreground-950">
          筛选条件
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 font-label text-xs text-accent-600 transition-colors hover:bg-accent-50"
        >
          <i className="ri-refresh-line"></i>
          重置
        </button>
      </div>

      <p className="font-label text-xs text-foreground-500">
        共找到 <span className="font-semibold text-accent-600">{resultCount}</span>{" "}
        部符合条件的电影
      </p>

      <div>
        <h3 className="mb-3 font-label text-sm font-semibold text-foreground-800">
          电影类型
        </h3>
        <div className="flex flex-wrap gap-2">
          {genreList.map((genre) => (
            <Chip
              key={genre}
              active={filters.genres.includes(genre)}
              onClick={() => onToggleGenre(genre)}
            >
              {genre}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-label text-sm font-semibold text-foreground-800">
          上映年份
        </h3>
        <div className="flex flex-wrap gap-2">
          <Chip
            active={filters.yearRange === "all"}
            onClick={() => onSetYear("all")}
          >
            全部年份
          </Chip>
          {yearRanges.map((range) => (
            <Chip
              key={range.label}
              active={filters.yearRange === range.label}
              onClick={() => onSetYear(range.label)}
            >
              {range.label}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-label text-sm font-semibold text-foreground-800">
          评分区间
        </h3>
        <div className="flex flex-wrap gap-2">
          {ratingOptions.map((opt) => (
            <Chip
              key={opt.value}
              active={filters.minRating === opt.value}
              onClick={() => onSetRating(opt.value)}
            >
              {opt.label}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-label text-sm font-semibold text-foreground-800">
          地区
        </h3>
        <div className="flex flex-wrap gap-2">
          <Chip
            active={filters.region === "all"}
            onClick={() => onSetRegion("all")}
          >
            全部地区
          </Chip>
          {regionList.map((region) => (
            <Chip
              key={region}
              active={filters.region === region}
              onClick={() => onSetRegion(region)}
            >
              {region}
            </Chip>
          ))}
        </div>
      </div>
    </aside>
  );
}